// src/components/TopicMafia/hooks/useTopicMafiaRoomApi.js
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../../api/axios";
import { DEFAULT_HOST_SETTINGS, dedupeSay } from "../topicMafiaUi";

const EMPTY_ARR = [];
const EMPTY_OBJ = {};

export default function useTopicMafiaRoomApi(roomId) {
    const [state, setState] = useState(null);

    // ✅ 브라우저 새로고침(F5)에도 타임라인 유지: sessionStorage 복구
    const storageKey = useMemo(() => `topicMafia:says:${roomId}`, [roomId]);
    const [says, setSays] = useState(() => {
        try {
            const raw = sessionStorage.getItem(storageKey);
            const parsed = raw ? JSON.parse(raw) : [];
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    });

    const [error, setError] = useState("");

    // host settings
    const [hostSettings, setHostSettings] = useState(DEFAULT_HOST_SETTINGS);
    const [savingHostSettings, setSavingHostSettings] = useState(false);

    // topic pairs
    const [topicPairs, setTopicPairs] = useState([]);
    const [topicPairsLoading, setTopicPairsLoading] = useState(false);

    const room = state?.room || null;

    // ✅ eslint 경고 방지: fallback 배열/객체는 고정 참조 사용
    const players = state?.players || EMPTY_ARR;
    const me = state?.me || EMPTY_OBJ;

    const myUserId = me?.user_id ?? null;
    const myPlayerId = me?.player_id ?? null;
    const joined = !!myPlayerId;

    const isHost = useMemo(() => {
        if (!room?.host_id || !myUserId) return false;
        return room.host_id === myUserId;
    }, [room?.host_id, myUserId]);

    const allReady = useMemo(() => {
        if (players.length === 0) return false;
        return players.every((p) => !!p.is_ready);
    }, [players]);

    const myReady = me?.is_ready ?? false;

    // ✅ says 변경 시 sessionStorage에 저장 (너무 커지면 최근 N개만 유지)
    useEffect(() => {
        try {
            const MAX = 500; // 필요하면 조절
            const trimmed = (says || []).slice(-MAX);
            sessionStorage.setItem(storageKey, JSON.stringify(trimmed));
        } catch {
            // ignore
        }
    }, [says, storageKey]);

    // ✅ 방이 바뀌면 이전 roomId의 타임라인이 섞이지 않도록 한번 더 보정
    useEffect(() => {
        try {
            const raw = sessionStorage.getItem(storageKey);
            const parsed = raw ? JSON.parse(raw) : [];
            if (Array.isArray(parsed)) setSays(parsed);
        } catch {
            // ignore
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storageKey]);

    // ─────────────────────────────────────────────
    // 공통: REST state 반영 + says 동기화 (서버 state에 says가 있을 때만)
    // ─────────────────────────────────────────────
    const applyState = useCallback((next) => {
        if (!next) return;

        setState(next);

        // ✅ next.says가 있을 때만 반영하되,
        //    빈 배열([])이면 기존 says를 유지(타임라인 보호)
        if (Array.isArray(next?.says) && next.says.length > 0) {
            setSays((prev) => dedupeSay([...prev, ...next.says]));
        }
    }, []);

    // ─────────────────────────────────────────────
    // REST
    // ─────────────────────────────────────────────
    const fetchState = useCallback(async () => {
        setError("");
        try {
            const res = await api.get(`/api/topic-mafia/rooms/${roomId}/state/`);
            if (res.data?.ok) applyState(res.data.state);
        } catch (e) {
            setError(e?.response?.data?.error || "상태를 불러오지 못했습니다.");
        }
    }, [roomId, applyState]);

    const postJoin = useCallback(async () => {
        setError("");
        try {
            const res = await api.post(`/api/topic-mafia/rooms/${roomId}/join/`);
            if (res.data?.ok) applyState(res.data.state);
        } catch (e) {
            setError(e?.response?.data?.error || "참가 실패");
        }
    }, [roomId, applyState]);

    const postLeave = useCallback(async () => {
        setError("");
        try {
            const res = await api.post(`/api/topic-mafia/rooms/${roomId}/leave/`);
            if (res.data?.ok) applyState(res.data.state);
        } catch (e) {
            setError(e?.response?.data?.error || "나가기 실패");
        }
    }, [roomId, applyState]);

    // ✅ A안: START 한 번으로 바로 PLAYING까지
    // ✅ "설정 반영"을 안 눌러도, 디폴트(hostSettings/room값)으로 시작 가능해야 함
    const postStart = useCallback(async () => {
        setError("");
        try {
            const payload = {};

            // 주제는 선택한 경우만 전송 (미선택이면 서버 랜덤/기본 처리 기대)
            if (hostSettings.topic_category_id) {
                payload.topic_category_id = hostSettings.topic_category_id;
            }

            if (isHost) {
                // ✅ hostSettings가 순간 비어있을 수 있으니 room 값도 fallback
                payload.max_players = hostSettings.max_players ?? room?.max_players;
                payload.total_rounds = hostSettings.total_rounds ?? room?.total_rounds;
                payload.minority_count = hostSettings.minority_count ?? room?.minority_count;
                payload.turn_time_limit_sec =
                    hostSettings.turn_time_limit_sec ?? room?.turn_time_limit_sec;
                payload.vote_time_limit_sec =
                    hostSettings.vote_time_limit_sec ?? room?.vote_time_limit_sec;
            }

            const res = await api.post(`/api/topic-mafia/rooms/${roomId}/start/`, payload);
            if (res.data?.ok) applyState(res.data.state);
        } catch (e) {
            setError(e?.response?.data?.error || "시작 실패(호스트만 가능)");
        }
    }, [
        roomId,
        isHost,
        hostSettings.topic_category_id,
        hostSettings.max_players,
        hostSettings.total_rounds,
        hostSettings.minority_count,
        hostSettings.turn_time_limit_sec,
        hostSettings.vote_time_limit_sec,
        room?.max_players,
        room?.total_rounds,
        room?.minority_count,
        room?.turn_time_limit_sec,
        room?.vote_time_limit_sec,
        applyState,
    ]);

    const postSay = useCallback(
        async (sayText) => {
            setError("");
            try {
                const res = await api.post(`/api/topic-mafia/rooms/${roomId}/say/`, { text: sayText });
                if (res.data?.ok) {
                    // ✅ state+says는 applyState로 통일
                    applyState(res.data.state);

                    // ✅ say 이벤트 단건은 추가로 append(서버가 state.says를 안 내려줄 수도 있으니 안전)
                    const s = res.data.say;
                    if (s) setSays((prev) => dedupeSay([...prev, s]));
                    return { ok: true };
                }
                return { ok: false };
            } catch (e) {
                setError(e?.response?.data?.error || "발언 실패");
                return { ok: false };
            }
        },
        [roomId, applyState]
    );

    const postVote = useCallback(
        async (targetPlayerId) => {
            setError("");
            try {
                const res = await api.post(`/api/topic-mafia/rooms/${roomId}/vote/`, {
                    target_player_id: targetPlayerId,
                });
                if (res.data?.ok) {
                    applyState(res.data.state);
                    return true;
                }
                return false;
            } catch (e) {
                setError(e?.response?.data?.error || "투표 실패");
                return false;
            }
        },
        [roomId, applyState]
    );

    const postResolve = useCallback(async () => {
        setError("");
        try {
            const res = await api.post(`/api/topic-mafia/rooms/${roomId}/resolve/`, {});
            if (res.data?.ok) applyState(res.data.state);
        } catch (e) {
            setError(e?.response?.data?.error || "투표 마감 실패(호스트만 가능)");
        }
    }, [roomId, applyState]);

    const postForceEnd = useCallback(
        async (reason) => {
            setError("");
            try {
                const res = await api.post(`/api/topic-mafia/rooms/${roomId}/force-end/`, { reason });
                if (res.data?.ok) applyState(res.data.state);
                return true;
            } catch (e) {
                setError(e?.response?.data?.error || "강제 종료 실패(호스트만 가능)");
                return false;
            }
        },
        [roomId, applyState]
    );

    // ✅ 강제 퇴장 (WAITING에서만 / 호스트만)
    const postKick = useCallback(
        async (targetPlayerId) => {
            setError("");
            try {
                const pid = Number(targetPlayerId);
                if (!pid) {
                    setError("퇴장 대상이 올바르지 않습니다.");
                    return false;
                }

                const res = await api.post(`/api/topic-mafia/rooms/${roomId}/kick/`, {
                    // ✅ 서버 구현이 뭐든 맞게 받도록 호환 키 같이 전송
                    target_player_id: pid,
                    player_id: pid,
                    target_id: pid,
                });

                if (res.data?.ok) {
                    applyState(res.data.state);
                    return true;
                }
                const msg = res.data?.error || "퇴장 실패";
                setError(msg);
                return false;
            } catch (e) {
                // ✅ 서버가 실제로 준 에러를 콘솔에 찍어두면 원인 즉시 파악됨
                console.error("kick error:", e?.response?.status, e?.response?.data);
                const msg = e?.response?.data?.error || "퇴장 실패(호스트만 가능)";
                setError(msg);
                return false;
            }
        },
        [roomId, applyState]
    );

    // ✅ READY 토글
    const postToggleReady = useCallback(async () => {
        if (!joined) return;
        setError("");
        try {
            const res = await api.post(`/api/topic-mafia/rooms/${roomId}/ready/`, {});
            if (res.data?.ok) applyState(res.data.state);
        } catch (e) {
            setError(e?.response?.data?.error || "준비 변경 실패");
        }
    }, [roomId, joined, applyState]);

    // ✅ room이 들어오면 "딱 1번만" hostSettings를 room 디폴트로 보정
    // - 이후 유저가 바꾼 값은 덮어쓰지 않음
    const initializedRef = useRef(false);
    useEffect(() => {
        if (!room) return;
        if (initializedRef.current) return;

        setHostSettings((prev) => ({
            ...prev,
            topic_category_id: room.topic_category_id ?? prev.topic_category_id ?? "",
            max_players: room.max_players ?? prev.max_players,
            total_rounds: room.total_rounds ?? prev.total_rounds,
            minority_count: room.minority_count ?? prev.minority_count,
            turn_time_limit_sec: room.turn_time_limit_sec ?? prev.turn_time_limit_sec,
            vote_time_limit_sec: room.vote_time_limit_sec ?? prev.vote_time_limit_sec,
        }));

        // ✅ BUGFIX: 딱 1번만 초기화되어야 하므로 true
        initializedRef.current = true;
    }, [room]);

    // ✅ 호스트 설정 저장 (WAITING에서만)
    const saveHostSettings = useCallback(async () => {
        setError("");
        setSavingHostSettings(true);
        try {
            const res = await api.post(`/api/topic-mafia/rooms/${roomId}/settings/`, {
                max_players: hostSettings.max_players,
                total_rounds: hostSettings.total_rounds,
                minority_count: hostSettings.minority_count,
                turn_time_limit_sec: hostSettings.turn_time_limit_sec,
                vote_time_limit_sec: hostSettings.vote_time_limit_sec,
                // ✅ 아래 2번 이슈까지 같이 반영(있다면)
                topic_refresh_mode: hostSettings.topic_refresh_mode,
                topic_category_id: hostSettings.topic_category_id,
            });

            if (res.data?.ok) {
                applyState(res.data.state);
                return { ok: true, state: res.data.state };
            }
            const msg = res.data?.error || "설정 저장 응답이 올바르지 않습니다.";
            setError(msg);
            return { ok: false, error: msg };
        } catch (e) {
            const msg = e?.response?.data?.error || "설정 저장 실패";
            setError(msg);
            return { ok: false, error: msg };
        } finally {
            setSavingHostSettings(false);
        }
    }, [
        roomId,
        hostSettings.max_players,
        hostSettings.total_rounds,
        hostSettings.minority_count,
        hostSettings.turn_time_limit_sec,
        hostSettings.vote_time_limit_sec,
        hostSettings.topic_refresh_mode,
        hostSettings.topic_category_id,
        applyState,
    ]);

    // topic pairs
    const fetchTopicPairs = useCallback(async () => {
        setTopicPairsLoading(true);
        try {
            const res = await api.get("/api/topic-mafia/topic-categories/");
            if (res.data?.ok) setTopicPairs(res.data.categories || []);
        } catch {
            // ignore
        } finally {
            setTopicPairsLoading(false);
        }
    }, []);

    // ✅ GUESS 제출
    const postGuess = useCallback(
        async (guessText) => {
            setError("");
            try {
                const res = await api.post(`/api/topic-mafia/rooms/${roomId}/guess/`, {
                    // ✅ 백엔드가 요구하는 키
                    guess_text: guessText,
                });

                if (res.data?.ok) {
                    applyState(res.data.state);
                    return { ok: true };
                }
                return { ok: false, error: res.data?.error || "추측 실패" };
            } catch (e) {
                const msg = e?.response?.data?.error || "추측 실패";
                setError(msg);
                return { ok: false, error: msg };
            }
        },
        [roomId, applyState]
    );

    // ✅ REMATCH (다시하기)
    // ⚠️ 엔드포인트는 백엔드에 맞춰야 함:
    //    예: /rematch/ 또는 /restart/ 또는 /reset/
    const postRematch = useCallback(async () => {
        setError("");
        try {
            const res = await api.post(`/api/topic-mafia/rooms/${roomId}/restart/`, {});
            if (res.data?.ok) {
                applyState(res.data.state);
                return true;
            }
            const msg = res.data?.error || "다시하기 실패";
            setError(msg);
            return false;
        } catch (e) {
            const msg = e?.response?.data?.error || "다시하기 실패";
            setError(msg);
            return false;
        }
    }, [roomId, applyState]);

    return {
        // raw state
        state,
        setState,
        says,
        setSays,
        error,
        setError,

        // derived
        room,
        players,
        me,
        joined,
        isHost,
        allReady,
        myReady,
        myUserId,
        myPlayerId,

        // host settings & topics
        hostSettings,
        setHostSettings,
        savingHostSettings,
        saveHostSettings,
        topicPairs,
        topicPairsLoading,
        fetchTopicPairs,

        // actions
        fetchState,
        postJoin,
        postLeave,
        postStart,
        postSay,
        postVote,
        postResolve,
        postForceEnd,
        postKick, // ✅ 추가
        postToggleReady,
        postGuess,
        postRematch, // ✅ 추가
    };
}
